import { useHttp } from "@inertiajs/react";
import type LocationsSuggestion from "@travily/types/models/LocationsSuggestion";
import type { StandardAutocompleteItem, StandardAutocompleteSection } from "@ui/components/StandardAutocomplete";
import { useDebouncedCallback } from "@ui/hooks/use-debounced-callback";
import { getCountryName } from "@ui/lib/format-date";
import { useCallback, useRef, useState } from "react";

interface SearchResponse {
  destinations: LocationsSuggestion[];
  locations: LocationsSuggestion[];
}

function toAutocompleteItem(suggestion: LocationsSuggestion): StandardAutocompleteItem {
  return {
    id: suggestion.mapbox_id,
    name: suggestion.name,
    description: suggestion.destination_id
      ? `${suggestion.name}, ${getCountryName(suggestion.country_code)}`
      : (suggestion.full_address ?? suggestion.name),
  };
}

function toSections(response: SearchResponse): StandardAutocompleteSection[] {
  const sections: StandardAutocompleteSection[] = [];

  if (response.destinations.length > 0) {
    sections.push({
      id: "destinations",
      title: "Destinations",
      items: response.destinations.map(toAutocompleteItem),
    });
  }

  if (response.locations.length > 0) {
    sections.push({
      id: "locations",
      title: "Other locations",
      items: response.locations.map(toAutocompleteItem),
    });
  }

  return sections;
}

interface UseLocationSearchReturn {
  inputValue: string;
  sections: StandardAutocompleteSection[];
  isLoading: boolean;
  selectedItem: StandardAutocompleteItem | null;
  onInputChange: (value: string) => void;
  onSelectionChange: (id: string | null) => void;
  setInitialValue: (item: StandardAutocompleteItem | null) => void;
}

export function useLocationSearch(): UseLocationSearchReturn {
  const http = useHttp<{ q: string }, SearchResponse>({ q: "" });
  const httpRef = useRef(http);
  httpRef.current = http;

  const [sections, setSections] = useState<StandardAutocompleteSection[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedItem, setSelectedItem] = useState<StandardAutocompleteItem | null>(null);
  const suggestionsRef = useRef<Map<string, LocationsSuggestion>>(new Map());

  const fetchResults = useDebouncedCallback(
    useCallback((query: string) => {
      if (query.length < 2) {
        setSections([]);
        return;
      }
      const h = httpRef.current;
      h.cancel();
      h.transform(() => ({ q: query }));
      h.get("/api/locations/search", {
        onSuccess: (response) => {
          const all = [...response.destinations, ...response.locations];
          suggestionsRef.current = new Map(all.map((s) => [s.mapbox_id, s]));
          setSections(toSections(response));
        },
      });
    }, []),
    300,
  );

  const onInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      if (selectedItem && value !== selectedItem.name) {
        setSelectedItem(null);
      }
      fetchResults(value);
    },
    [selectedItem, fetchResults],
  );

  const onSelectionChange = useCallback((id: string | null) => {
    if (id === null) {
      setSelectedItem(null);
      return;
    }
    const suggestion = suggestionsRef.current.get(id);
    if (suggestion) {
      setSelectedItem({ id: suggestion.mapbox_id, name: suggestion.name });
      setInputValue(suggestion.name);
      setSections([]);
    }
  }, []);

  const setInitialValue = useCallback((item: StandardAutocompleteItem | null) => {
    setSelectedItem(item);
    setInputValue(item?.name ?? "");
    setSections([]);
  }, []);

  return {
    inputValue,
    sections,
    isLoading: http.processing,
    selectedItem,
    onInputChange,
    onSelectionChange,
    setInitialValue,
  };
}
